import React, { ErrorInfo } from "react"
import { AlertTriangle } from "lucide-react"
import { tokens } from "@/design-system/tokens"
import { Button } from "@/components/atoms/Button"
import { reportFrontendError } from "@/utils/errorReporter"
import { isChunkLoadError } from "@/lib/lazyWithRetry"
import { hasUnsavedWork } from "@/lib/unsavedWork"

// A chunk-load failure caught this high (above the per-route RouteBoundary) means
// a lazy import in the app shell / sidebar / layout failed after a deploy — which
// otherwise blanks the ENTIRE app (sidebar + content) with a dead "Something went
// wrong" until a manual reload. Auto-reload instead (guarded against loops) so a
// deploy never strands an open tab. Mirrors RouteBoundary's recovery.
// Returns true if it scheduled a reload (→ show the calm "Updating…" screen).
// Does NOT auto-reload when there are unsaved edits — reloading would destroy the
// user's in-progress work; instead the calm screen offers a manual Reload button.
function scheduleChunkReload(): boolean {
  if (hasUnsavedWork()) return true // show "Updating…" + manual button, never auto-reload
  const KEY = "tenon_chunk_reload"
  let st = { n: 0, at: 0 }
  try { st = JSON.parse(sessionStorage.getItem(KEY) || "") } catch { /* fresh */ }
  const now = Date.now()
  if (!st.at || now - st.at > 30000) st = { n: 0, at: 0 } // fresh, unrelated failure
  if (st.n >= 4) return false // give up after 4 attempts — show the fallback instead
  sessionStorage.setItem(KEY, JSON.stringify({ n: st.n + 1, at: now }))
  const delay = st.n === 0 ? 0 : Math.min(st.n * 1500, 4500)
  setTimeout(() => window.location.reload(), delay)
  return true
}

interface ErrorBoundaryProps {
  children: React.ReactNode
  /** Static node, or a render function that receives the caught error so the
   *  fallback can surface it (e.g. RouteBoundary's expandable details). */
  fallback?: React.ReactNode | ((error?: Error, errorInfo?: ErrorInfo) => React.ReactNode)
  onReset?: () => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
  errorInfo?: ErrorInfo
  chunkReloading?: boolean
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error, chunkReloading: isChunkLoadError(error) }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Chunk-load failures are deploy-window transients, not bugs: auto-reload to
    // fetch the new shell and don't spam the error reporter. If we've already
    // reloaded too many times, fall through to the fallback (chunkReloading=false).
    if (isChunkLoadError(error)) {
      const reloading = scheduleChunkReload()
      this.setState({ errorInfo, chunkReloading: reloading })
      return
    }

    console.error("Uncaught error:", error, errorInfo)
    this.setState({ errorInfo, chunkReloading: false })

    // Report render errors to the platform — these are critical (full crash)
    reportFrontendError({
      type: "render_error",
      message: error.message,
      stack: (error.stack ?? "") + "\n\nComponent Stack:\n" + (errorInfo.componentStack ?? ""),
      url: window.location.href,
    })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined })
    this.props.onReset?.()
  }

  render() {
    if (this.state.hasError) {
      const { fallback } = this.props

      // Chunk-load transient: show a calm "Updating…" screen (not the red crash
      // card) so a deploy looks seamless. If there are unsaved edits we did NOT
      // auto-reload — tell the user so they can save first, then reload manually.
      if (this.state.chunkReloading) {
        const unsaved = hasUnsavedWork()
        return (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            height: "100vh", padding: "20px", textAlign: "center",
            background: tokens.color.background.surface, color: tokens.color.text.secondary,
          }}>
            <p style={{ fontSize: 15, maxWidth: 420 }}>
              {unsaved
                ? "A new version is ready. You have unsaved changes — save them first, then reload."
                : "Updating to the latest version…"}
            </p>
            <div style={{ marginTop: 16 }}>
              <Button variant="secondary" onClick={() => window.location.reload()}>
                Reload now
              </Button>
            </div>
          </div>
        )
      }

      if (fallback) {
        return typeof fallback === "function"
          ? fallback(this.state.error, this.state.errorInfo)
          : fallback
      }

      return (
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          padding: "20px",
          textAlign: "center",
          background: tokens.color.background.surface,
        }}>
          <div style={{
            background: "#EF444422",
            color: "#EF4444",
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "20px",
          }}>
            <AlertTriangle size={48} />
          </div>
          <h1 style={{
            fontSize: "24px",
            color: tokens.color.text.primary,
            marginBottom: "10px",
          }}>Something went wrong</h1>
          <p style={{
            color: tokens.color.text.secondary,
            marginBottom: "20px",
            maxWidth: "400px",
          }}>
            We encountered an unexpected error. Please try again or contact support.
          </p>
          <div style={{ display: "flex", gap: "10px" }}>
            <Button
              variant="secondary"
              onClick={() => window.location.href = "/"}
            >
              Go Home
            </Button>
            <Button
              variant="primary"
              onClick={this.handleReset}
            >
              Try Again
            </Button>
          </div>
          {this.state.error && (
            <details 
              style={{ 
                marginTop: "20px", 
                padding: "10px", 
                background: tokens.color.background.elevated, 
                borderRadius: "6px",
                maxWidth: "600px",
                width: "100%",
                textAlign: "left"
              }}
            >
              <summary style={{ cursor: "pointer", color: tokens.color.text.secondary }}>
                Error Details
              </summary>
              <pre style={{ 
                whiteSpace: "pre-wrap", 
                wordBreak: "break-word", 
                fontSize: "12px", 
                color: tokens.color.text.muted 
              }}>
                {this.state.error.toString()}
                <br />
                {this.state.error.stack}
              </pre>
            </details>
          )}
        </div>
      )
    }

    return this.props.children
  }
}
